import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Statistic, Spin, message, Result, Button } from 'antd';
import { apiFetch } from '../api';

interface StatsData {
  total_comments: number;
  matched_comments: number;
  unmatched_comments: number;
  negative_comments: number;
  active_rules: number;
  by_status: Record<string, number>;
  by_brand: Record<string, number>;
  by_platform: Record<string, number>;
  by_day: { date: string; count: number }[];
}

const statusColors: Record<string, string> = {
  new: '#1677ff', processed: '#fa8c16', published: '#52c41a', ignored: '#bfbfbf',
};

const Bars: React.FC<{ data: Record<string, number>; colors?: Record<string, string> }> = ({ data, colors }) => {
  const entries = Object.entries(data);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div>
      {entries.length === 0 && <span style={{ color: '#aaa' }}>нет данных</span>}
      {entries.map(([k, v]) => (
        <div key={k} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span>{k}</span><span>{v}</span>
          </div>
          <div style={{ background: '#f0f0f0', borderRadius: 4, height: 14 }}>
            <div style={{ width: `${(v / max) * 100}%`, background: (colors && colors[k]) || '#1677ff', height: 14, borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const DayChart: React.FC<{ data: { date: string; count: number }[] }> = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 180, paddingTop: 8 }}>
      {data.map((d) => (
        <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }} title={`${d.date}: ${d.count}`}>
          <div style={{ fontSize: 11 }}>{d.count}</div>
          <div style={{ width: '70%', background: '#1677ff', borderRadius: '4px 4px 0 0', height: `${(d.count / max) * 140}px`, minHeight: d.count ? 2 : 0 }} />
          <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>{d.date.slice(5)}</div>
        </div>
      ))}
    </div>
  );
};

const Stats: React.FC = () => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setStats(await apiFetch<StatsData>('/stats/'));
    } catch (e: any) {
      setError(e.message || 'Не удалось загрузить статистику');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (error) {
    return <Result status="warning" title="Не удалось загрузить статистику" subTitle={error}
      extra={<Button type="primary" onClick={load}>Повторить</Button>} />;
  }
  if (loading || !stats) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  const statCol = { xs: 12, sm: 12, md: 8, lg: 5, xl: 5 } as const;

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col {...statCol}><Card><Statistic title="Всего комментариев" value={stats.total_comments} /></Card></Col>
        <Col {...statCol}><Card><Statistic title="С правилом" value={stats.matched_comments} /></Card></Col>
        <Col {...statCol}><Card><Statistic title="Без совпадения" value={stats.unmatched_comments} /></Card></Col>
        <Col {...statCol}><Card><Statistic title="Негатив" value={stats.negative_comments} styles={{ content: { color: '#cf1322' } }} /></Card></Col>
        <Col xs={12} sm={12} md={8} lg={4}><Card><Statistic title="Активных правил" value={stats.active_rules} /></Card></Col>
      </Row>
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="Комментарии за последние 14 дней">
            <DayChart data={stats.by_day} />
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={8}><Card title="По статусу"><Bars data={stats.by_status} colors={statusColors} /></Card></Col>
        <Col xs={24} md={8}><Card title="По бренду"><Bars data={stats.by_brand} /></Card></Col>
        <Col xs={24} md={8}><Card title="По площадке"><Bars data={stats.by_platform} /></Card></Col>
      </Row>
    </>
  );
};

export default Stats;
